// frontend-react/src/hooks/useFormValidation.js
/**
 * Hook pour validation de formulaires avec Zod
 *
 * Usage:
 * const { errors, validate, validateField, clearErrors } = useFormValidation(schema)
 *
 * // Validation complète avant submit
 * const result = validate(formData)
 * if (result.success) { ... submit result.data ... }
 *
 * // Validation d'un champ à la volée (onBlur)
 * validateField('email', emailValue)
 */

import { useState, useCallback } from 'react';

export function useFormValidation(schema) {
  const [errors, setErrors] = useState({});

  // Valide tout le formulaire, retourne {success, data, errors}
  const validate = useCallback(
    (data) => {
      const result = schema.safeParse(data);

      if (result.success) {
        setErrors({});
        return { success: true, data: result.data, errors: {} };
      }

      const fieldErrors = {};
      for (const issue of result.error.issues) {
        const path = issue.path.join('.');
        if (!fieldErrors[path]) {
          fieldErrors[path] = issue.message;
        }
      }

      setErrors(fieldErrors);
      return { success: false, data: null, errors: fieldErrors };
    },
    [schema]
  );

  // Valide un seul champ (pour validation onBlur)
  const validateField = useCallback(
    (fieldName, value) => {
      // Créer un objet partiel avec juste ce champ
      const partialData = { [fieldName]: value };

      // On utilise pick si disponible, sinon on valide le champ individuellement
      const fieldSchema = schema.shape?.[fieldName];
      if (!fieldSchema) {
        return { success: true };
      }

      const result = fieldSchema.safeParse(value);

      setErrors((prev) => {
        const next = { ...prev };
        if (result.success) {
          delete next[fieldName];
        } else {
          next[fieldName] = result.error.issues[0]?.message || 'Valeur invalide';
        }
        return next;
      });

      return { success: result.success, error: result.error?.issues[0]?.message };
    },
    [schema]
  );

  // Efface toutes les erreurs
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Efface l'erreur d'un champ spécifique
  const clearFieldError = useCallback((fieldName) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  // Définit une erreur manuellement (utile pour erreurs serveur)
  const setFieldError = useCallback((fieldName, message) => {
    setErrors((prev) => ({ ...prev, [fieldName]: message }));
  }, []);

  return {
    errors,
    validate,
    validateField,
    clearErrors,
    clearFieldError,
    setFieldError,
    hasErrors: Object.keys(errors).length > 0,
  };
}

export default useFormValidation;
